import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import DispositionTag from "../components/DispositionTag";
import {
  CalendarClock,
  CheckCircle2,
  Phone,
  Mail,
  Flame,
  RotateCw,
} from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { toast } from "sonner";

export default function FollowUps() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/follow-ups");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const updateFollowUpAt = async (id, iso) => {
    try {
      await api.patch(`/calls/${id}`, { follow_up_at: iso || null });
      toast.success(iso ? "Follow-up date saved" : "Follow-up cleared");
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  const markDone = async (id) => {
    try {
      await api.patch(`/calls/${id}`, { follow_up_done: true });
      toast.success("Marked done");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const scheduleCallback = async (id, iso) => {
    if (!iso) return;
    try {
      await api.post(`/calls/${id}/schedule-callback`, { when: iso });
      toast.success("Ashley will call back");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule");
    }
  };

  const cancelCallback = async (id) => {
    try {
      await api.delete(`/calls/${id}/schedule-callback`);
      toast.success("Callback cancelled");
      load();
    } catch {
      toast.error("Could not cancel");
    }
  };

  const hotLeads = items.filter((i) => i.disposition === "Hot Lead");
  const gotEmail = items.filter((i) => i.disposition === "Got Email");
  const other = items.filter(
    (i) => !["Hot Lead", "Got Email"].includes(i.disposition)
  );

  return (
    <div data-testid="follow-ups-page">
      <div className="mb-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
          Pipeline
        </div>
        <h1 className="text-3xl font-medium tracking-tight text-white">
          Follow-up Manager
        </h1>
        <p className="text-sm text-white/50 mt-2">
          Every Hot Lead & Got Email call stays here until you mark it done.
          Schedule Ashley to auto-redial or set a manual follow-up date.
        </p>
      </div>

      {loading && <div className="text-white/40 text-sm">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="bg-[#121214] border border-dashed border-white/10 rounded-xl p-16 text-center">
          <CheckCircle2 className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-white font-medium text-lg mb-2">All caught up</h3>
          <p className="text-white/50 text-sm">
            No open follow-ups. New Hot Leads and Got Email calls appear here automatically.
          </p>
        </div>
      )}

      {!loading && (hotLeads.length > 0 || gotEmail.length > 0 || other.length > 0) && (
        <div className="space-y-8">
          <FollowUpSection
            label="Hot Leads"
            count={hotLeads.length}
            icon={Flame}
            items={hotLeads}
            onFollowUpAtChange={updateFollowUpAt}
            onDone={markDone}
            onScheduleCallback={scheduleCallback}
            onCancelCallback={cancelCallback}
            onOpen={(c) => nav(`/calls?open=${c.id}`)}
          />
          <FollowUpSection
            label="Got Email"
            count={gotEmail.length}
            icon={Mail}
            items={gotEmail}
            onFollowUpAtChange={updateFollowUpAt}
            onDone={markDone}
            onScheduleCallback={scheduleCallback}
            onCancelCallback={cancelCallback}
            onOpen={(c) => nav(`/calls?open=${c.id}`)}
          />
          {other.length > 0 && (
            <FollowUpSection
              label="Other (manual follow-up scheduled)"
              count={other.length}
              icon={CalendarClock}
              items={other}
              onFollowUpAtChange={updateFollowUpAt}
              onDone={markDone}
              onScheduleCallback={scheduleCallback}
              onCancelCallback={cancelCallback}
              onOpen={(c) => nav(`/calls?open=${c.id}`)}
            />
          )}
        </div>
      )}
    </div>
  );
}

const FollowUpSection = ({
  label,
  count,
  icon: Icon,
  items,
  onFollowUpAtChange,
  onDone,
  onScheduleCallback,
  onCancelCallback,
  onOpen,
}) => {
  if (items.length === 0) return null;
  return (
    <section data-testid={`follow-up-section-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-4 h-4 text-[#FFB000]" strokeWidth={1.75} />
        <h2 className="text-sm font-medium text-white tracking-tight">{label}</h2>
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
          {count}
        </span>
      </div>
      <div className="bg-[#121214] border border-white/10 rounded-xl divide-y divide-white/5 overflow-hidden">
        {items.map((c) => (
          <FollowUpRow
            key={c.id}
            call={c}
            onFollowUpAtChange={onFollowUpAtChange}
            onDone={onDone}
            onScheduleCallback={onScheduleCallback}
            onCancelCallback={onCancelCallback}
            onOpen={onOpen}
          />
        ))}
      </div>
    </section>
  );
};

const toLocalInputValue = (iso) => {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? parseISO(iso) : iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

const localInputToIso = (v) => {
  if (!v) return null;
  return new Date(v).toISOString();
};

const FollowUpRow = ({
  call,
  onFollowUpAtChange,
  onDone,
  onScheduleCallback,
  onCancelCallback,
  onOpen,
}) => {
  const [followUpLocal, setFollowUpLocal] = useState(toLocalInputValue(call.follow_up_at));
  const [callbackLocal, setCallbackLocal] = useState(
    toLocalInputValue(call.prospect_scheduled_callback_at)
  );
  useEffect(() => {
    setFollowUpLocal(toLocalInputValue(call.follow_up_at));
    setCallbackLocal(toLocalInputValue(call.prospect_scheduled_callback_at));
  }, [call.id, call.follow_up_at, call.prospect_scheduled_callback_at]);

  const hasCallback = !!call.prospect_scheduled_callback_at && !call.prospect_callback_done;

  return (
    <div
      data-testid={`follow-up-row-${call.id}`}
      className="px-5 py-4 hover:bg-white/[0.02] transition-colors"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-[220px]">
          <button
            onClick={() => onOpen(call)}
            data-testid={`follow-up-open-${call.id}`}
            className="text-left"
          >
            <div className="text-sm text-white/90 font-medium hover:text-[#FFB000] transition-colors">
              {call.prospect_name || "Unknown"}
            </div>
          </button>
          <div className="text-xs font-mono text-white/60 mt-0.5">
            {call.prospect_phone}
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <DispositionTag value={call.disposition} />
            <span className="text-[10px] font-mono text-white/40">
              · Called{" "}
              {call.created_at
                ? formatDistanceToNow(parseISO(call.created_at), { addSuffix: true })
                : "—"}
            </span>
          </div>
          {call.summary && (
            <div className="text-xs text-white/50 mt-2 max-w-[380px] line-clamp-2">
              {call.summary}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-[440px] flex flex-wrap gap-3 items-end justify-end">
          {/* Follow-up date */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-white/40 block mb-1">
              Follow up by
            </label>
            <div className="flex items-center gap-1.5">
              <input
                data-testid={`follow-up-at-input-${call.id}`}
                type="datetime-local"
                value={followUpLocal}
                onChange={(e) => setFollowUpLocal(e.target.value)}
                className="bg-[#0a0a0b] border border-white/10 rounded-md text-white text-xs px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
              />
              <button
                data-testid={`follow-up-save-${call.id}`}
                onClick={() => onFollowUpAtChange(call.id, localInputToIso(followUpLocal))}
                className="bg-white/[0.03] hover:bg-white/[0.08] text-white/80 border border-white/10 text-xs px-2.5 py-1.5 rounded-md"
              >
                Save
              </button>
            </div>
          </div>

          {/* Ashley callback */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-white/40 block mb-1">
              Ashley auto-callback
            </label>
            <div className="flex items-center gap-1.5">
              <input
                data-testid={`callback-at-input-${call.id}`}
                type="datetime-local"
                value={callbackLocal}
                onChange={(e) => setCallbackLocal(e.target.value)}
                className="bg-[#0a0a0b] border border-white/10 rounded-md text-white text-xs px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/50"
              />
              {!hasCallback ? (
                <button
                  data-testid={`schedule-callback-${call.id}`}
                  onClick={() => onScheduleCallback(call.id, localInputToIso(callbackLocal))}
                  className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5"
                >
                  <RotateCw className="w-3 h-3" />
                  Schedule
                </button>
              ) : (
                <button
                  data-testid={`cancel-callback-${call.id}`}
                  onClick={() => onCancelCallback(call.id)}
                  className="bg-white/[0.03] hover:bg-white/[0.08] text-white/60 border border-white/10 text-xs px-2.5 py-1.5 rounded-md"
                >
                  Cancel
                </button>
              )}
            </div>
            {hasCallback && (
              <div className="text-[10px] font-mono text-emerald-400/70 mt-1">
                Scheduled{" "}
                {format(parseISO(call.prospect_scheduled_callback_at), "MMM d · HH:mm")}
              </div>
            )}
          </div>

          {/* Mark done */}
          <button
            data-testid={`mark-done-${call.id}`}
            onClick={() => onDone(call.id)}
            className="bg-white/[0.03] hover:bg-[#FFB000]/10 text-white/70 hover:text-[#FFB000] hover:border-[#FFB000]/30 border border-white/10 text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 self-end"
            title="Remove from follow-up queue"
          >
            <CheckCircle2 className="w-3 h-3" />
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
